import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getNotes, getClients, getProjects, createNote, updateNote, deleteNote } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, FileText, Calendar, Briefcase, Users, Search, Bold, Italic, Heading2, List, ListOrdered, Minus } from 'lucide-react';
import { formatDate } from '../lib/utils';

// ─── Formatting toolbar ──────────────────────────────────────────────────────

const ToolbarButton = ({ onClick, title, children }) => (
  <button
    type="button"
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    title={title}
    className="p-1.5 hover:bg-gray-200 rounded transition-colors text-gray-700 hover:text-black"
  >
    {children}
  </button>
);

const useFormatting = (textareaRef, content, setContent) => {
  const insert = useCallback((before, after = '', defaultText = '') => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = content.slice(start, end) || defaultText;
    const newContent = content.slice(0, start) + before + selected + after + content.slice(end);
    setContent(newContent);
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + before.length, start + before.length + selected.length);
    });
  }, [textareaRef, content, setContent]);

  const insertLinePrefix = useCallback((prefix) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = content.lastIndexOf('\n', start - 1) + 1;
    const alreadyHas = content.slice(lineStart).startsWith(prefix);
    const newContent = alreadyHas
      ? content.slice(0, lineStart) + content.slice(lineStart + prefix.length)
      : content.slice(0, lineStart) + prefix + content.slice(lineStart);
    setContent(newContent);
    requestAnimationFrame(() => {
      el.focus();
      const offset = alreadyHas ? -prefix.length : prefix.length;
      el.setSelectionRange(start + offset, start + offset);
    });
  }, [textareaRef, content, setContent]);

  const insertNumberedList = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const lineStart = content.lastIndexOf('\n', start - 1) + 1;
    const newContent = content.slice(0, lineStart) + '1. ' + content.slice(lineStart);
    setContent(newContent);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + 3, start + 3); });
  }, [textareaRef, content, setContent]);

  return { insert, insertLinePrefix, insertNumberedList };
};

const wordCount = (text) => text.trim() ? text.trim().split(/\s+/).length : 0;

const Notes = () => {
  const [notes, setNotes] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [filterProject, setFilterProject] = useState('all');
  const textareaRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    client_id: '',
    project_id: '',
    meeting_date: '',
  });

  const setContent = (content) => setFormData(f => ({ ...f, content }));
  const { insert, insertLinePrefix, insertNumberedList } = useFormatting(textareaRef, formData.content, setContent);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notesRes, clientsRes, projectsRes] = await Promise.all([
        getNotes(),
        getClients(),
        getProjects(),
      ]);
      setNotes(notesRes.data);
      setClients(clientsRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      toast.error('Chyba při načítání poznámek');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        client_id: formData.client_id || null,
        project_id: formData.project_id || null,
        meeting_date: formData.meeting_date || null,
      };
      if (editingNote) {
        await updateNote(editingNote.id, data);
        toast.success('Poznámka aktualizována');
      } else {
        await createNote(data);
        toast.success('Poznámka vytvořena');
      }
      setDialogOpen(false);
      setEditingNote(null);
      setFormData({ title: '', content: '', client_id: '', project_id: '', meeting_date: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Chyba při ukládání');
    }
  };

  const handleEdit = (note) => {
    setEditingNote(note);
    setFormData({
      title: note.title,
      content: note.content,
      client_id: note.client_id || '',
      project_id: note.project_id || '',
      meeting_date: note.meeting_date || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!noteToDelete) return;
    try {
      await deleteNote(noteToDelete.id);
      toast.success('Poznámka smazána');
      fetchData();
    } catch (error) {
      toast.error('Chyba při mazání');
    }
    setDeleteDialogOpen(false);
    setNoteToDelete(null);
  };

  // Filter notes
  const filteredNotes = notes.filter((note) => {
    const matchesSearch = 
      note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      note.content.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClient = filterClient === 'all' || note.client_id === filterClient;
    const matchesProject = filterProject === 'all' || note.project_id === filterProject;
    return matchesSearch && matchesClient && matchesProject;
  });

  // Get projects filtered by selected client in form
  const filteredProjects = projects
    .filter(p => p.status === 'active')
    .filter(p => !formData.client_id || p.client_id === formData.client_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="font-oswald text-xl uppercase tracking-wide">Načítání...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="notes-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-oswald text-4xl font-bold uppercase tracking-tight">Poznámky</h1>
          <p className="font-serif text-gray-600 mt-1">Záznamy ze schůzek a poznámky k projektům</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[#F4E600] text-black font-oswald uppercase tracking-wide border-2 border-black rounded-none shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all"
              onClick={() => {
                setEditingNote(null);
                setFormData({ title: '', content: '', client_id: '', project_id: '', meeting_date: '' });
              }}
              data-testid="add-note-btn"
            >
              <Plus size={18} className="mr-2" />
              Nová poznámka
            </Button>
          </DialogTrigger>
          <DialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] max-w-4xl w-full h-[90vh] flex flex-col p-0 gap-0">
            <DialogHeader className="px-6 pt-6 pb-4 border-b-2 border-gray-100 flex-shrink-0">
              <DialogTitle className="font-oswald text-xl uppercase tracking-tight">
                {editingNote ? 'Upravit poznámku' : 'Nová poznámka'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
              {/* Název */}
              <div className="px-6 pt-4 flex-shrink-0">
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Název poznámky…"
                  className="border-0 border-b-2 border-gray-200 focus:border-black rounded-none text-2xl font-oswald font-bold uppercase tracking-tight px-0 h-auto py-2 shadow-none focus-visible:ring-0"
                  data-testid="note-title-input"
                  autoFocus
                />
              </div>

              {/* Metadata řádek */}
              <div className="px-6 py-3 flex flex-wrap gap-3 items-center border-b border-gray-100 flex-shrink-0">
                <div className="flex items-center gap-2 min-w-[140px]">
                  <Calendar size={14} className="text-gray-400 flex-shrink-0" />
                  <Input
                    type="date"
                    value={formData.meeting_date}
                    onChange={(e) => setFormData({ ...formData, meeting_date: e.target.value })}
                    className="border-0 border-b border-gray-200 rounded-none text-sm font-serif px-0 h-7 shadow-none focus-visible:ring-0 w-36"
                    data-testid="note-meeting-date-input"
                  />
                </div>
                <div className="flex items-center gap-2 min-w-[160px]">
                  <Users size={14} className="text-gray-400 flex-shrink-0" />
                  <Select
                    value={formData.client_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, client_id: v === 'none' ? '' : v, project_id: '' })}
                  >
                    <SelectTrigger className="border-0 border-b border-gray-200 rounded-none h-7 text-sm font-serif shadow-none focus:ring-0 w-44 px-0" data-testid="note-client-select">
                      <SelectValue placeholder="Klient" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bez klienta</SelectItem>
                      {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-2 min-w-[160px]">
                  <Briefcase size={14} className="text-gray-400 flex-shrink-0" />
                  <Select
                    value={formData.project_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, project_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger className="border-0 border-b border-gray-200 rounded-none h-7 text-sm font-serif shadow-none focus:ring-0 w-44 px-0" data-testid="note-project-select">
                      <SelectValue placeholder="Projekt" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Bez projektu</SelectItem>
                      {filteredProjects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Toolbar */}
              <div className="px-6 py-2 flex items-center gap-1 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                <ToolbarButton onClick={() => insert('**', '**', 'tučný text')} title="Tučně (Ctrl+B)">
                  <Bold size={15} />
                </ToolbarButton>
                <ToolbarButton onClick={() => insert('*', '*', 'kurzíva')} title="Kurzíva (Ctrl+I)">
                  <Italic size={15} />
                </ToolbarButton>
                <ToolbarButton onClick={() => insertLinePrefix('## ')} title="Nadpis">
                  <Heading2 size={15} />
                </ToolbarButton>
                <div className="w-px h-5 bg-gray-300 mx-1" />
                <ToolbarButton onClick={() => insertLinePrefix('- ')} title="Odrážky">
                  <List size={15} />
                </ToolbarButton>
                <ToolbarButton onClick={insertNumberedList} title="Číslovaný seznam">
                  <ListOrdered size={15} />
                </ToolbarButton>
                <div className="w-px h-5 bg-gray-300 mx-1" />
                <ToolbarButton onClick={() => insert('\n---\n')} title="Oddělovač">
                  <Minus size={15} />
                </ToolbarButton>
                <div className="ml-auto text-xs text-gray-400 font-serif">
                  {wordCount(formData.content)} slov
                </div>
              </div>

              {/* Textarea */}
              <div className="flex-1 overflow-hidden px-6 py-4">
                <textarea
                  ref={textareaRef}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      if (e.key === 'b') { e.preventDefault(); insert('**', '**', 'tučný text'); }
                      if (e.key === 'i') { e.preventDefault(); insert('*', '*', 'kurzíva'); }
                    }
                  }}
                  required
                  placeholder="Začni psát poznámku…&#10;&#10;Tipy:&#10;## Nadpis&#10;**tučně**, *kurzíva*&#10;- odrážka"
                  className="w-full h-full resize-none border-0 outline-none font-serif text-base leading-relaxed text-gray-800 placeholder-gray-300 bg-transparent"
                  data-testid="note-content-input"
                />
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t-2 border-gray-100 flex justify-end gap-2 flex-shrink-0">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-none border-2 font-oswald uppercase">
                  Zrušit
                </Button>
                <Button
                  type="submit"
                  className="bg-black text-white font-oswald uppercase tracking-wide rounded-none hover:bg-gray-800 border-2 border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]"
                  data-testid="note-submit-btn"
                >
                  {editingNote ? 'Uložit změny' : 'Vytvořit poznámku'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filters */}
      <div className="bg-white border-2 border-black p-4 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-wrap gap-4 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="font-oswald uppercase text-xs tracking-wide">Hledat</Label>
          <div className="relative">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Hledat v poznámkách..."
              className="pl-10 border-2 border-gray-200 focus:border-black rounded-none"
              data-testid="notes-search-input"
            />
          </div>
        </div>
        <div className="w-48">
          <Label className="font-oswald uppercase text-xs tracking-wide">Klient</Label>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="border-2 border-gray-200 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Všichni klienti</SelectItem>
              {clients.map((c) => (
                <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <Label className="font-oswald uppercase text-xs tracking-wide">Projekt</Label>
          <Select value={filterProject} onValueChange={setFilterProject}>
            <SelectTrigger className="border-2 border-gray-200 rounded-none">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Všechny projekty</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Notes Grid */}
      {filteredNotes.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-gray-300 p-12 text-center">
          <FileText size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="font-oswald text-lg uppercase tracking-wide text-gray-500">
            Žádné poznámky
          </h3>
          <p className="font-serif text-gray-400 mt-2">
            {searchQuery || filterClient !== 'all' || filterProject !== 'all'
              ? 'Zkuste změnit filtry'
              : 'Vytvořte první poznámku'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredNotes.map((note) => (
            <div
              key={note.id}
              className="bg-white border-2 border-black p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all group"
              data-testid={`note-card-${note.id}`}
            >
              <div className="flex items-start justify-between mb-3">
                <h3 className="font-oswald text-lg font-bold uppercase tracking-tight line-clamp-2">
                  {note.title}
                </h3>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleEdit(note)}
                    className="p-1.5 hover:bg-gray-100 transition-colors"
                    data-testid={`edit-note-${note.id}`}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => {
                      setNoteToDelete(note);
                      setDeleteDialogOpen(true);
                    }}
                    className="p-1.5 hover:bg-red-100 text-red-500 transition-colors"
                    data-testid={`delete-note-${note.id}`}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {note.meeting_date && (
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                  <Calendar size={14} />
                  <span className="font-serif">{note.meeting_date}</span>
                </div>
              )}

              <div className="flex flex-wrap gap-2 mb-3">
                {note.client_name && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#006CB6]/10 text-[#006CB6] text-xs font-oswald uppercase">
                    <Users size={12} />
                    {note.client_name}
                  </span>
                )}
                {note.project_name && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 bg-[#F4E600]/30 text-black text-xs font-oswald uppercase">
                    <Briefcase size={12} />
                    {note.project_name}
                  </span>
                )}
              </div>

              <p className="font-serif text-gray-600 text-sm line-clamp-4 whitespace-pre-wrap">
                {note.content}
              </p>

              <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
                <span className="font-serif">{note.created_by_name}</span>
                <span className="font-serif">{formatDate(note.created_at)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="border-2 border-black rounded-none shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-oswald uppercase">Smazat poznámku?</AlertDialogTitle>
            <AlertDialogDescription className="font-serif">
              Opravdu chcete smazat poznámku &quot;{noteToDelete?.title}&quot;? Tuto akci nelze vrátit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-oswald uppercase rounded-none border-2 border-gray-200">
              Zrušit
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 font-oswald uppercase rounded-none hover:bg-red-600"
            >
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Notes;
